import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  TextField,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Book as BookIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Person as TeacherIcon,
  AdminPanelSettings as CoordIcon,
  Refresh as RefreshIcon,
  AssignmentTurnedIn as ReviewIcon,
  ArrowBack as BackIcon,
  CalendarToday as CalendarIcon,
  EventNote as EventNoteIcon,
  Visibility as ViewIcon,
  Timeline as TimelineIcon,
  School as SchoolIcon,
  Grade as GradeIcon,
  DateRange as DateRangeIcon,
  HolidayVillage as HolidayIcon,
} from '@mui/icons-material';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import type { UnitWiseSyllabus } from '../types/curriculum.types.ts';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import BoardClassSubjectSelector from './Boardclasssubjectselector.tsx';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { curriculumSelectionAtom, knowledgeMapsAtom } from './Curriculumselectionatom.ts';


// ─── Types ────────────────────────────────────────────────────────────────────


type UserRole = 'teacher' | 'coordinator';
type TermStatus = 'draft' | 'submitted' | 'approved' | 'rejected';


interface Day {
  id: string;
  dayOfWeek: string;
  objectives: string[];
  activities: string[];
  materials: string[];
  homework?: string;
  isHoliday: boolean;
  holidayType?: string;
  holidayReason?: string;
  isReviewDay: boolean;
  notes?: string;
  customTitle?: string;
}


interface Week {
  id: string;
  name: string;
  weekNumber?: number;
  days: Day[];
  subtopics: any[];
  totalMarks?: number;
  notes?: string;
}


interface Topic {
  id: string;
  name: string;
  description?: string;
  duration: number;
  weeks: Week[];
  marks?: number;
  prerequisites?: { name: string }[];
  learning_material?: string[];
}


interface Unit {
  id: string;
  name: string;
  topics: Topic[];
  totalWeeks?: number;
}


interface Subtopic {
  subtopic: string;
  marks: number;
}


interface KnowledgeMapTopic {
  topic: string;
  description?: string;
  subtopics: Subtopic[];
}


interface KnowledgeMap {
  board: string;
  class: string;
  subject: string;
  topics: KnowledgeMapTopic[];
  units?: Unit[];
}


interface CurriculumUnit {
  id: string;
  name: string;
  subtopics: string[];
  marks: number;
  weeksEstimate: number;
  description?: string;
  weeklyPlan?: {
    weekNumber: number;
    weekName: string;
    days: {
      dayOfWeek: string;
      objectives: string[];
      activities: string[];
      materials: string[];
      homework: string;
      isHoliday: boolean;
      holidayReason?: string;
      isReviewDay: boolean;
    }[];
  }[];
}


interface CurriculumTerm {
  termIndex: number;
  name: string;
  units: CurriculumUnit[];
  status: TermStatus;
  coordinatorComment?: string;
  submittedAt?: string;
  approvedAt?: string;
}


interface CurriculumPlannerProps {
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
  onBackToKnowledgeMap?: (board: string, classVal: string, subject: string) => void;
  onProceedToStudyPlan?: (board: string, classVal: string, subject: string) => void;
}


// ─── Helpers ──────────────────────────────────────────────────────────────────


function topicToUnit(topic: Topic, index: number, unitId?: string): CurriculumUnit {
  const subtopicNames: string[] = [];
  let totalMarks = 0;
  let weeksEstimate = topic.weeks?.length || 1;
  let weeklyPlan: CurriculumUnit['weeklyPlan'] = [];


  // Extract weeks and days
  if (topic.weeks && topic.weeks.length > 0) {
    weeksEstimate = topic.weeks.length;
   
    topic.weeks.forEach((week, wi) => {
      const dayData = week.days?.map((day) => ({
        dayOfWeek: day.dayOfWeek,
        objectives: day.objectives || [],
        activities: day.activities || [],
        materials: day.materials || [],
        homework: day.homework || '',
        isHoliday: day.isHoliday || false,
        holidayReason: day.holidayReason || '',
        isReviewDay: day.isReviewDay || false,
      })) || [];
     
      weeklyPlan?.push({
        weekNumber: wi + 1,
        weekName: week.name || `Week ${wi + 1}`,
        days: dayData,
      });
    });


    // Get subtopics from weeks
    topic.weeks.forEach(week => {
      week.subtopics?.forEach((st: any) => {
        if (st.name) {
          subtopicNames.push(st.name);
          totalMarks += (st.marks || 0);
        }
      });
    });
  }


  // If no subtopics found, use topic name
  if (subtopicNames.length === 0) {
    subtopicNames.push(topic.name);
  }


  return {
    id: unitId || `unit_${index}`,
    name: topic.name,
    subtopics: subtopicNames,
    marks: totalMarks || topic.marks || 0,
    weeksEstimate: weeksEstimate || 1,
    description: topic.description,
    weeklyPlan,
  };
}


function enrichTermsWithWeeklyPlans(terms: CurriculumTerm[], topics: Topic[]): CurriculumTerm[] {
  // Create a map of topic names to topics
  const topicMap: Record<string, Topic> = {};
  topics.forEach(topic => {
    topicMap[topic.name] = topic;
  });


  return terms.map(term => ({
    ...term,
    units: term.units.map(unit => {
      // Find matching topic
      const matchingTopic = topicMap[unit.name];
      if (matchingTopic) {
        // Convert topic to unit with weekly plan
        return topicToUnit(matchingTopic, 0, unit.id);
      }
      return unit;
    }),
  }));
}


function buildTerms(km: KnowledgeMap | any): CurriculumTerm[] {
  const topics = km.topics ?? [];
  if (topics.length === 0) return [];


  // If we have units from API, use them
  if (km.units && km.units.length > 0) {
    const units: CurriculumUnit[] = [];
    km.units.forEach((unit: Unit, ui: number) => {
      unit.topics.forEach((topic, ti) => {
        const converted = topicToUnit(topic, ti, unit.id || `unit_${ui}_${ti}`);
        units.push(converted);
      });
    });


    const mid = Math.ceil(units.length / 2);
    const split1 = units.slice(0, mid);
    const split2 = units.slice(mid);


    const result = [
      {
        termIndex: 0,
        name: 'Term 1',
        units: split1,
        status: 'draft' as const,
        coordinatorComment: '',
      },
      {
        termIndex: 1,
        name: 'Term 2',
        units: split2,
        status: 'draft' as const,
        coordinatorComment: '',
      },
    ].filter((t) => t.units.length > 0);


    return result;
  }


  // Fallback: split topics into two terms
  const mid = Math.ceil(topics.length / 2);
  const splits = [topics.slice(0, mid), topics.slice(mid)];


  return splits
    .map((slice, ti) => ({
      termIndex: ti,
      name: `Term ${ti + 1}`,
      units: slice.map((t, i) => topicToUnit(t, ti * 100 + i)),
      status: 'draft' as const,
      coordinatorComment: '',
    }))
    .filter((t) => t.units.length > 0);
}


function mergeSavedState(terms: CurriculumTerm[], savedTerms: CurriculumTerm[] | null | undefined): CurriculumTerm[] {
  if (!savedTerms?.length) return terms;
  return terms.map((term) => {
    const saved = savedTerms.find((s) => s.termIndex === term.termIndex);
    if (!saved) return term;
    return {
      ...term,
      status: saved.status,
      coordinatorComment: saved.coordinatorComment,
      submittedAt: saved.submittedAt,
      approvedAt: saved.approvedAt,
    };
  });
}


function totalWeeks(units: CurriculumUnit[]) {
  return units.reduce((s, u) => s + u.weeksEstimate, 0);
}


function totalMarks(units: CurriculumUnit[]) {
  return units.reduce((s, u) => s + u.marks, 0);
}


// ─── Status Chip Component ───────────────────────────────────────────────────


function StatusChip({ status }: { status: TermStatus }) {
  const CONFIG: Record<TermStatus, { label: string; color: 'default' | 'warning' | 'success' | 'error'; icon: React.ReactElement }> = {
    draft: { label: 'Draft', color: 'default', icon: <EditIcon sx={{ fontSize: 16 }} /> },
    submitted: { label: 'Under Review', color: 'warning', icon: <ReviewIcon sx={{ fontSize: 16 }} /> },
    approved: { label: 'Approved', color: 'success', icon: <CheckIcon sx={{ fontSize: 16 }} /> },
    rejected: { label: 'Revision Needed', color: 'error', icon: <RejectIcon sx={{ fontSize: 16 }} /> },
  };
  const { label, color, icon } = CONFIG[status];
  return (
    <Chip
      label={label}
      color={color}
      size="medium"
      icon={icon}
      sx={{ fontWeight: 700, fontSize: '0.7rem', height: 28, px: 1 }}
    />
  );
}


// ─── Day-wise Weekly Plan Component ─────────────────────────────────────────


function WeeklyPlanView({ weeklyPlan }: { weeklyPlan?: CurriculumUnit['weeklyPlan'] }) {
  const [expandedWeeks, setExpandedWeeks] = useState<Record<number, boolean>>({});


  if (!weeklyPlan || weeklyPlan.length === 0) {
    return (
      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#94A3B8', py: 1 }}>
        No weekly plan available
      </Typography>
    );
  }


  const toggleWeek = (weekIndex: number) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [weekIndex]: !prev[weekIndex],
    }));
  };


  const DAY_COLORS: Record<string, { bg: string; color: string }> = {
    Monday: { bg: '#EFF6FF', color: '#1E40AF' },
    Tuesday: { bg: '#F0FDF4', color: '#065F46' },
    Wednesday: { bg: '#FEFCE8', color: '#854D0E' },
    Thursday: { bg: '#FDF2F8', color: '#9D174D' },
    Friday: { bg: '#F3E8FF', color: '#6B21A8' },
    Saturday: { bg: '#FEF2F2', color: '#991B1B' },
    Sunday: { bg: '#F8FAFC', color: '#475569' },
  };


  const DAY_LABELS: Record<string, string> = {
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
    Sunday: 'Sun',
  };


  return (
    <Box sx={{ mt: 1.5 }}>
      {weeklyPlan.map((week, wi) => {
        const hasHolidays = week.days.some(d => d.isHoliday);
        const workingDays = week.days.filter(d => !d.isHoliday);
        const holidayDays = week.days.filter(d => d.isHoliday);
        const totalObjectives = week.days.reduce((sum, d) => sum + d.objectives.length, 0);
        const isExpanded = expandedWeeks[wi] || false;


        return (
          <Paper
            key={wi}
            elevation={0}
            sx={{
              mb: 1.5,
              border: '1px solid #E2E8F0',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#FAFBFC',
            }}
          >
            {/* Week Header - Click to Expand */}
            <Box
              sx={{
                px: 2.5,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#F8FAFC',
                borderBottom: '1px solid #E2E8F0',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#F1F5F9' },
              }}
              onClick={() => toggleWeek(wi)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  label={`Week ${wi + 1}`}
                  size="medium"
                  sx={{ fontSize: '0.65rem', height: 24, bgcolor: '#8B5CF6', color: 'white', fontWeight: 700 }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                  {week.weekName}
                </Typography>
                <Chip
                  label={`${workingDays.length} days`}
                  size="small"
                  sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#E0E7FF', color: '#3730A3' }}
                />
                <Chip
                  label={`${totalObjectives} objectives`}
                  size="small"
                  sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#D1FAE5', color: '#065F46' }}
                />
                {hasHolidays && (
                  <Chip
                    label={`${holidayDays.length} holidays`}
                    size="small"
                    sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#FEE2E2', color: '#991B1B' }}
                  />
                )}
              </Box>
              <IconButton size="small" sx={{ p: 0.5 }}>
                {isExpanded ? (
                  <ExpandLessIcon sx={{ fontSize: 22 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 22 }} />
                )}
              </IconButton>
            </Box>


            {/* Day-wise Table - Expandable */}
            <Collapse in={isExpanded}>
              <Box sx={{ p: 2 }}>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Day</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Objectives</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Activities</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Materials</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Homework</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1, textAlign: 'center' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {week.days.map((day, di) => {
                        const dayColor = DAY_COLORS[day.dayOfWeek] || { bg: '#F8FAFC', color: '#475569' };
                        const isHoliday = day.isHoliday;


                        return (
                          <TableRow
                            key={di}
                            sx={{
                              bgcolor: isHoliday ? '#FEF2F2' : (di % 2 === 0 ? 'white' : '#FAFBFC'),
                              opacity: isHoliday ? 0.7 : 1,
                            }}
                          >
                            <TableCell sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Chip
                                  label={DAY_LABELS[day.dayOfWeek] || day.dayOfWeek.substring(0, 3)}
                                  size="small"
                                  sx={{
                                    fontSize: '0.55rem',
                                    height: 22,
                                    bgcolor: isHoliday ? '#EF4444' : dayColor.bg,
                                    color: isHoliday ? 'white' : dayColor.color,
                                    fontWeight: 700,
                                  }}
                                />
                                {day.isReviewDay && !isHoliday && (
                                  <Chip
                                    label="Review"
                                    size="small"
                                    sx={{ fontSize: '0.45rem', height: 16, bgcolor: '#FEF3C7', color: '#92400E' }}
                                  />
                                )}
                                {isHoliday && (
                                  <Chip
                                    label="🎉"
                                    size="small"
                                    sx={{ fontSize: '0.45rem', height: 16, bgcolor: '#FEE2E2' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              {isHoliday ? (
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#991B1B', fontWeight: 600 }}>
                                  {day.holidayReason || 'Holiday'}
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {day.objectives.length > 0 ? (
                                    day.objectives.map((obj, oi) => (
                                      <Chip
                                        key={oi}
                                        label={obj}
                                        size="small"
                                        sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#D1FAE5' }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="body2" sx={{ fontSize: '0.6rem', color: '#94A3B8' }}>
                                      —
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              {!isHoliday && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {day.activities.length > 0 ? (
                                    day.activities.map((act, ai) => (
                                      <Chip
                                        key={ai}
                                        label={act}
                                        size="small"
                                        sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#E0E7FF' }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="body2" sx={{ fontSize: '0.6rem', color: '#94A3B8' }}>
                                      —
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              {!isHoliday && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {day.materials.length > 0 ? (
                                    day.materials.map((mat, mi) => (
                                      <Chip
                                        key={mi}
                                        label={mat}
                                        size="small"
                                        sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#FEF3C7' }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="body2" sx={{ fontSize: '0.6rem', color: '#94A3B8' }}>
                                      —
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              {!isHoliday && day.homework ? (
                                <Chip
                                  label={`📝 ${day.homework}`}
                                  size="small"
                                  sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#F3E8FF' }}
                                />
                              ) : (
                                <Typography variant="body2" sx={{ fontSize: '0.6rem', color: '#94A3B8' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1, textAlign: 'center' }}>
                              {isHoliday ? (
                                <Chip
                                  label="Holiday"
                                  size="small"
                                  sx={{ fontSize: '0.5rem', height: 18, bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 600 }}
                                />
                              ) : day.isReviewDay ? (
                                <Chip
                                  label="Review"
                                  size="small"
                                  sx={{ fontSize: '0.5rem', height: 18, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600 }}
                                />
                              ) : (
                                <Chip
                                  label="Active"
                                  size="small"
                                  sx={{ fontSize: '0.5rem', height: 18, bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600 }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
}


// ─── Read-only Unit List ─────────────────────────────────────────────────────


function ReadonlyUnitList({ units }: { units: CurriculumUnit[] }) {
  const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>({});


  const toggleUnit = (unitId: string) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitId]: !prev[unitId],
    }));
  };


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {units.map((unit, idx) => {
        const hasWeeklyPlan = unit.weeklyPlan && unit.weeklyPlan.length > 0;
        const isUnitExpanded = expandedUnits[unit.id] || false;
        const totalDays = unit.weeklyPlan?.reduce((sum, w) => sum + w.days.length, 0) || 0;
        const totalHolidays = unit.weeklyPlan?.reduce((sum, w) => sum + w.days.filter(d => d.isHoliday).length, 0) || 0;


        return (
          <Paper
            key={unit.id}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              },
            }}
          >
            {/* Unit Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                '&:hover': { bgcolor: '#F8FAFC' },
              }}
              onClick={() => toggleUnit(unit.id)}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: '#DBEAFE',
                  color: '#1E40AF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </Box>


              <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                {unit.name}
              </Typography>


              <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, flexWrap: 'wrap' }}>
                <Chip
                  label={`${unit.weeksEstimate} weeks`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.55rem' }}
                />
                <Chip
                  label={`${unit.marks} marks`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.55rem', bgcolor: '#FEF3C7', color: '#92400E' }}
                />
                <Chip
                  label={`${unit.subtopics.length} subtopics`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.55rem' }}
                />
                {hasWeeklyPlan && (
                  <Chip
                    icon={<ViewIcon sx={{ fontSize: 12 }} />}
                    label={`${totalDays} days`}
                    size="small"
                    sx={{ height: 20, fontSize: '0.55rem', bgcolor: '#E0E7FF', color: '#3730A3' }}
                  />
                )}
                {totalHolidays > 0 && (
                  <Chip
                    label={`${totalHolidays} holidays`}
                    size="small"
                    sx={{ height: 20, fontSize: '0.55rem', bgcolor: '#FEE2E2', color: '#991B1B' }}
                  />
                )}
              </Box>


              {hasWeeklyPlan && (
                <IconButton size="small" sx={{ p: 0.5 }}>
                  {isUnitExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              )}
            </Box>


            {/* Weekly Plan - Expandable */}
            {hasWeeklyPlan && (
              <Collapse in={isUnitExpanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <WeeklyPlanView weeklyPlan={unit.weeklyPlan} />
                </Box>
              </Collapse>
            )}
          </Paper>
        );
      })}
    </Box>
  );
}


// ─── Term Card Component ─────────────────────────────────────────────────────


function TermCard({ term, role, onSubmit, onApprove, onReject, onRequestEdit }: {
  term: CurriculumTerm;
  role: UserRole;
  onSubmit: () => void;
  onApprove: (comment: string) => void;
  onReject: (comment: string) => void;
  onRequestEdit: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [comment, setComment] = useState(term.coordinatorComment ?? '');
  const isTeacher = role === 'teacher';
  const isCoordinator = role === 'coordinator';
  const weeks = totalWeeks(term.units);
  const marks = totalMarks(term.units);
  const totalDays = term.units.reduce((sum, u) => sum + (u.weeklyPlan?.reduce((s, w) => s + w.days.length, 0) || 0), 0);
  const totalHolidays = term.units.reduce((sum, u) => sum + (u.weeklyPlan?.reduce((s, w) => s + w.days.filter(d => d.isHoliday).length, 0) || 0), 0);
  const TERM_COLORS = ['#1E40AF', '#065F46'];
  const TERM_BG = ['#DBEAFE', '#D1FAE5'];
  const ti = term.termIndex;


  const canSubmit = isTeacher && (term.status === 'draft' || term.status === 'rejected');


  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((v) => !v)}
      elevation={0}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: term.status === 'approved' ? '#86EFAC' : term.status === 'rejected' ? '#FCA5A5' : '#E2E8F0',
        borderRadius: '12px !important',
        '&:before': { display: 'none' },
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 22 }} />}
        sx={{
          bgcolor: term.status === 'approved' ? '#F0FDF4' : term.status === 'rejected' ? '#FFF5F5' : '#FAFAFA',
          minHeight: 56,
          borderRadius: '12px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: TERM_BG[ti] ?? '#E5E7EB',
            color: TERM_COLORS[ti] ?? '#374151',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <BookIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>{term.name}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {term.units.length} units · {weeks} weeks · {marks} marks
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label={`${totalDays} days`}
              size="small"
              sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#E0E7FF' }}
            />
            {totalHolidays > 0 && (
              <Chip
                label={`${totalHolidays} holidays`}
                size="small"
                sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#FEE2E2', color: '#991B1B' }}
              />
            )}
          </Box>
          <StatusChip status={term.status} />
        </Box>
      </AccordionSummary>


      <AccordionDetails sx={{ pt: 2, px: 2.5, pb: 2.5 }}>
        <Alert
          severity="info"
          sx={{
            mb: 2,
            py: 1,
            fontSize: '0.75rem',
            borderRadius: 1.5,
            '& .MuiAlert-icon': { fontSize: '1.2rem' },
          }}
        >
          <strong>Read-only view:</strong> To edit topics or subtopics, click "Request Edit" to go back to Knowledge Map Manager.
        </Alert>


        {term.coordinatorComment && term.status !== 'draft' && (
          <Alert
            severity={term.status === 'rejected' ? 'error' : 'success'}
            sx={{
              mb: 2,
              py: 1,
              fontSize: '0.75rem',
              borderRadius: 1.5,
            }}
          >
            <strong>Coordinator:</strong> {term.coordinatorComment}
          </Alert>
        )}


        <ReadonlyUnitList units={term.units} />


        {/* Subtopics Tags */}
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {term.units.flatMap((u) => u.subtopics.slice(0, 4)).map((st) => (
            <Chip
              key={st}
              label={st}
              size="small"
              sx={{ height: 22, fontSize: '0.6rem', bgcolor: '#F9FAFB' }}
            />
          ))}
          {term.units.flatMap(u => u.subtopics).length > 4 && (
            <Chip
              label={`+${term.units.flatMap(u => u.subtopics).length - 4} more`}
              size="small"
              sx={{ height: 22, fontSize: '0.6rem' }}
            />
          )}
        </Box>


        <Divider sx={{ my: 2 }} />


        {/* Actions */}
        {isTeacher && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canSubmit && (
              <Button
                variant="contained"
                size="medium"
                startIcon={<SendIcon />}
                onClick={onSubmit}
                sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36 }}
              >
                Submit for Approval
              </Button>
            )}
            <Button
              variant="outlined"
              size="medium"
              startIcon={<EditIcon />}
              onClick={onRequestEdit}
              sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36 }}
            >
              Request Edit
            </Button>
            {term.status === 'submitted' && (
              <Button
                variant="outlined"
                size="medium"
                disabled
                startIcon={<LockIcon />}
                sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36 }}
              >
                Awaiting Review
              </Button>
            )}
            {term.status === 'approved' && (
              <Button
                variant="outlined"
                size="medium"
                disabled
                startIcon={<CheckIcon />}
                color="success"
                sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36 }}
              >
                Published
              </Button>
            )}
          </Box>
        )}


        {isCoordinator && term.status === 'submitted' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              size="medium"
              multiline
              rows={3}
              fullWidth
              placeholder="Review comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="medium"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => onApprove(comment)}
                sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36 }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                size="medium"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => onReject(comment)}
                sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36 }}
              >
                Request Revision
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}


// ─── Main Component ──────────────────────────────────────────────────────────


export default function CurriculumPlanner({
  initialBoard,
  initialClass,
  initialSubject,
  onBackToKnowledgeMap,
  onProceedToStudyPlan,
}: CurriculumPlannerProps) {
  const { showSnackBar } = useSnackBar();
  const aiActions = AiActions();
  const [role, setRole] = useState<UserRole>('teacher');
  const knowledgeMaps = useRecoilValue(knowledgeMapsAtom);
  const [loading, setLoading] = useState(false);
  const selection = useRecoilValue(curriculumSelectionAtom);
  const selectedBoard = selection.board;
  const selectedClass = selection.classVal;
  const selectedSubject = selection.subject;
  const [terms, setTerms] = useState<CurriculumTerm[]>([]);
  const [syllabusId, setSyllabusId] = useState<string | null>(null);
  const [syllabusTopics, setSyllabusTopics] = useState<Topic[]>([]);
  const setCurriculumSelection = useSetRecoilState(curriculumSelectionAtom);


  useEffect(() => {
    if (initialBoard && initialClass && initialSubject) {
      setCurriculumSelection((prev) => ({
        ...prev,
        board: initialBoard,
        classVal: initialClass,
        subject: initialSubject,
      }));
    }
  }, [initialBoard, initialClass, initialSubject]);


  const currentKnowledgeMap = selectedBoard && selectedClass && selectedSubject
    ? knowledgeMaps.find(km => km.board === selectedBoard && km.class === selectedClass && km.subject === selectedSubject) ?? null
    : null;


  useEffect(() => {
    if (!currentKnowledgeMap) {
      setTerms([]);
      setSyllabusId(null);
      setSyllabusTopics([]);
      return;
    }


    const loadSyllabusTerms = async () => {
      try {
        setLoading(true);
        const kmId = (currentKnowledgeMap as KnowledgeMap & { id?: string; _id?: string }).id
          ?? (currentKnowledgeMap as KnowledgeMap & { _id?: string })._id;
       
        const syllabusList = await aiActions.getSyllabusList(kmId);
       
        if (syllabusList?.length > 0) {
          const syllabus = syllabusList[0] as UnitWiseSyllabus & { topics?: Topic[] };
          setSyllabusId(syllabus._id);
         
          // Store topics for enriching terms
          if (syllabus.topics) {
            setSyllabusTopics(syllabus.topics);
          }
         
          // Build fresh terms from topics
          let freshTerms: CurriculumTerm[] = [];
         
          if (syllabus.topics) {
            // Create a knowledge map with topics
            const kmWithTopics = {
              ...currentKnowledgeMap,
              topics: syllabus.topics,
            };
            freshTerms = buildTerms(kmWithTopics);
           
            // Enrich terms with weekly plans from topics
            freshTerms = enrichTermsWithWeeklyPlans(freshTerms, syllabus.topics);
          } else if (currentKnowledgeMap.topics) {
            freshTerms = buildTerms(currentKnowledgeMap);
          }
         
          // Merge saved status
          if (syllabus.terms) {
            setTerms(mergeSavedState(freshTerms, syllabus.terms as CurriculumTerm[]));
          } else {
            setTerms(freshTerms);
          }
        } else {
          setSyllabusId(null);
          if (currentKnowledgeMap.topics) {
            const freshTerms = buildTerms(currentKnowledgeMap);
            setTerms(freshTerms);
          }
        }
      } catch (err) {
        console.error(err);
        if (currentKnowledgeMap.topics) {
          setTerms(buildTerms(currentKnowledgeMap));
        }
      } finally {
        setLoading(false);
      }
    };


    loadSyllabusTerms();
  }, [currentKnowledgeMap, selectedBoard, selectedClass, selectedSubject]);


  const persistTermsToApi = async (updatedTerms: CurriculumTerm[], status?: string) => {
    if (!currentKnowledgeMap || !selectedBoard || !selectedClass || !selectedSubject) return;


    const kmId = (currentKnowledgeMap as KnowledgeMap & { id?: string; _id?: string }).id
      ?? (currentKnowledgeMap as KnowledgeMap & { _id?: string })._id;
    const payload: Record<string, unknown> = {
      knowledge_map_id: kmId,
      board: selectedBoard,
      class: selectedClass,
      subject: selectedSubject,
      terms: updatedTerms,
    };
    if (status) payload.status = status;


    if (syllabusId) {
      await aiActions.updateSyllabus(syllabusId, payload);
    } else {
      const created = await aiActions.createSyllabus({ ...payload, status: status ?? 'draft' });
      const newId = created?._id ?? created?.data?._id;
      if (newId) setSyllabusId(newId);
    }
  };


  const submitTerm = async (termIndex: number) => {
    const updatedTerms = terms.map((t) =>
      t.termIndex === termIndex
        ? { ...t, status: 'submitted' as const, submittedAt: new Date().toISOString() }
        : t
    );
    setTerms(updatedTerms);
    try {
      await persistTermsToApi(updatedTerms, 'submitted');
      showSnackBar(`Term ${termIndex + 1} submitted for review`, 'info');
    } catch {
      showSnackBar('Failed to submit term', 'error');
    }
  };


  const approveTerm = async (termIndex: number, comment: string) => {
    const updatedTerms = terms.map((t) =>
      t.termIndex === termIndex
        ? { ...t, status: 'approved' as const, coordinatorComment: comment, approvedAt: new Date().toISOString() }
        : t
    );
    setTerms(updatedTerms);
    const allApproved = updatedTerms.every((t) => t.status === 'approved');
    try {
      await persistTermsToApi(updatedTerms, allApproved ? 'approved' : 'submitted');
      showSnackBar(`Term ${termIndex + 1} approved`, 'success');
    } catch {
      showSnackBar('Failed to approve term', 'error');
    }
  };


  const rejectTerm = async (termIndex: number, comment: string) => {
    const updatedTerms = terms.map((t) =>
      t.termIndex === termIndex
        ? { ...t, status: 'rejected' as const, coordinatorComment: comment }
        : t
    );
    setTerms(updatedTerms);
    try {
      await persistTermsToApi(updatedTerms, 'draft');
      showSnackBar(`Revision requested for Term ${termIndex + 1}`, 'warning');
    } catch {
      showSnackBar('Failed to reject term', 'error');
    }
  };


  const handleRequestEdit = () => {
    if (onBackToKnowledgeMap && selectedBoard && selectedClass && selectedSubject) {
      onBackToKnowledgeMap(selectedBoard, selectedClass, selectedSubject);
    }
  };


  const totalTopics = currentKnowledgeMap?.topics?.length ?? 0;
  const totalWeeksSum = terms.reduce((s, t) => s + totalWeeks(t.units), 0);
  const totalMarksSum = terms.reduce((s, t) => s + totalMarks(t.units), 0);
  const totalDaysSum = terms.reduce((sum, t) => sum + t.units.reduce((s, u) => s + (u.weeklyPlan?.reduce((ws, w) => ws + w.days.length, 0) || 0), 0), 0);
  const totalHolidaysSum = terms.reduce((sum, t) => sum + t.units.reduce((s, u) => s + (u.weeklyPlan?.reduce((ws, w) => ws + w.days.filter(d => d.isHoliday).length, 0) || 0), 0), 0);


  const breadcrumbItems = [
    { label: 'Dashboard' },
    { label: 'Curriculum Planner' },
    ...(selectedBoard ? [{ label: selectedBoard }] : []),
    ...(selectedClass ? [{ label: `Class ${selectedClass}` }] : []),
    ...(selectedSubject ? [{ label: selectedSubject }] : []),
  ];


  const anySubmitted = terms.some(t => t.status === 'submitted');
  const allApproved = terms.length > 0 && terms.every(t => t.status === 'approved');


  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F5F7FA', minHeight: '100vh', paddingLeft: { md: '150px', sm: '100%' } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <DynamicBreadcrumbs items={breadcrumbItems} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {onBackToKnowledgeMap && (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<BackIcon />}
              onClick={handleRequestEdit}
              sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36, px: 2 }}
            >
              Back to Edit
            </Button>
          )}
          <ToggleButtonGroup
            value={role}
            exclusive
            size="medium"
            onChange={(_e, val) => val && setRole(val)}
            sx={{
              '& .MuiToggleButton-root': {
                fontSize: '0.75rem',
                gap: 0.5,
                py: 0.75,
                px: 1.5,
              }
            }}
          >
            <ToggleButton value="teacher">
              <TeacherIcon sx={{ fontSize: 18 }} /> Teacher
            </ToggleButton>
            <ToggleButton value="coordinator">
              <CoordIcon sx={{ fontSize: 18 }} /> Coordinator
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>


      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={32} />
        </Box>
      )}


      {/* Selection Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2.5,
          border: '1px solid #E2E8F0',
          borderRadius: 2,
          bgcolor: 'white',
        }}
      >
        <BoardClassSubjectSelector
          externalLoading={loading}
          onSelectionComplete={(selection) => {
            console.log('Selection:', selection);
          }}
          colProps={{ xs: 12, sm: 3 }}
        />
      </Paper>


      {/* Approval Workflow */}
      {terms.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2.5,
            border: '1px solid #E2E8F0',
            borderRadius: 2,
            bgcolor: 'white',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.75rem', mb: 1.5, color: '#475569', display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon sx={{ fontSize: 18 }} />
            Approval Workflow
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {[
              { label: 'Build Plan', icon: <EditIcon sx={{ fontSize: 18 }} /> },
              { label: 'Submit', icon: <SendIcon sx={{ fontSize: 18 }} /> },
              { label: 'Review', icon: <ReviewIcon sx={{ fontSize: 18 }} /> },
              { label: 'Approved', icon: <CheckIcon sx={{ fontSize: 18 }} /> },
            ].map((step, i) => {
              const done = i === 0 ? terms.length > 0 : i === 1 ? anySubmitted : i === 2 ? anySubmitted : allApproved;
              const isActive = i === 0 ? true : i === 1 ? anySubmitted : i === 2 ? anySubmitted : allApproved;
              return (
                <React.Fragment key={i}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: done ? '#1565C0' : 'grey.100',
                      color: done ? '#fff' : 'text.disabled',
                      transition: 'all 0.3s ease',
                      border: isActive ? '2px solid #1565C0' : 'none',
                    }}>
                      {step.icon}
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', mt: 0.5, textAlign: 'center', fontWeight: done ? 700 : 400 }}>
                      {step.label}
                    </Typography>
                    {done && (
                      <CheckIcon sx={{ fontSize: 14, color: '#1565C0', mt: 0.25 }} />
                    )}
                  </Box>
                  {i < 3 && (
                    <Box sx={{
                      flex: 1,
                      height: 2,
                      bgcolor: done && (i === 0 ? anySubmitted : i === 1 ? anySubmitted : allApproved) ? '#1565C0' : 'grey.200',
                      transition: 'all 0.3s ease',
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Paper>
      )}


      {/* Alerts */}
      {!loading && (!selectedBoard || !selectedClass || !selectedSubject) && (
        <Alert
          severity="info"
          sx={{ mb: 2.5, fontSize: '0.8rem', borderRadius: 2, py: 1.5 }}
        >
          Select a Board, Class, and Subject to load the curriculum.
        </Alert>
      )}
      {!loading && selectedBoard && selectedClass && selectedSubject && !currentKnowledgeMap && (
        <Alert
          severity="warning"
          sx={{ mb: 2.5, fontSize: '0.8rem', borderRadius: 2, py: 1.5 }}
        >
          No knowledge map found for {selectedBoard} — Class {selectedClass} — {selectedSubject}.
        </Alert>
      )}


      {/* Stats */}
      {terms.length > 0 && (
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          {[
            { label: 'Topics', value: totalTopics, icon: <SchoolIcon sx={{ fontSize: 18 }} />, color: '#3B82F6' },
            { label: 'Weeks', value: totalWeeksSum, icon: <DateRangeIcon sx={{ fontSize: 18 }} />, color: '#22C55E' },
            { label: 'Days', value: totalDaysSum, icon: <CalendarIcon sx={{ fontSize: 18 }} />, color: '#8B5CF6' },
            { label: 'Holidays', value: totalHolidaysSum, icon: <HolidayIcon sx={{ fontSize: 18 }} />, color: '#EF4444' },
            { label: 'Marks', value: totalMarksSum, icon: <GradeIcon sx={{ fontSize: 18 }} />, color: '#F59E0B' },
            { label: 'Terms', value: terms.length, icon: <BookIcon sx={{ fontSize: 18 }} />, color: '#6366F1' },
          ].map(s => (
            <Grid item xs={6} sm={4} md={2} key={s.label}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  textAlign: 'center',
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  bgcolor: 'white',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                  <Box sx={{ color: s.color }}>{s.icon}</Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: s.color, fontSize: '1.4rem' }}>
                    {s.value}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#64748B' }}>
                  {s.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}


      {/* Terms */}
      {terms.map(term => (
        <TermCard
          key={term.termIndex}
          term={term}
          role={role}
          onSubmit={() => submitTerm(term.termIndex)}
          onApprove={(comment) => approveTerm(term.termIndex, comment)}
          onReject={(comment) => rejectTerm(term.termIndex, comment)}
          onRequestEdit={handleRequestEdit}
        />
      ))}


      {/* Proceed to Study Plan */}
      {allApproved && onProceedToStudyPlan && selectedBoard && selectedClass && selectedSubject && (
        <Alert
          severity="success"
          sx={{
            mt: 3,
            fontSize: '0.85rem',
            borderRadius: 2,
            py: 1.5,
            '& .MuiAlert-action': { alignItems: 'center' },
          }}
          action={
            <Button
              color="inherit"
              size="medium"
              variant="outlined"
              onClick={() => onProceedToStudyPlan(selectedBoard, selectedClass, selectedSubject)}
              sx={{ fontSize: '0.8rem', textTransform: 'none' }}
            >
              Proceed to Study Plan →
            </Button>
          }
        >
          🎉 All terms approved! Continue to generate the week-wise study plan.
        </Alert>
      )}
    </Box>
  );
}

